/**
 * Shared WebSocket authentication utilities.
 *
 * Reuses the JWT already issued by the existing REST login flow
 * (see `src/utils/auth.js`, stored in the `authToken` cookie) so the Orbiee
 * agent WebSocket authenticates the same way as the rest of the app instead
 * of duplicating token lookup / login-redirect logic per call site.
 */
import { getAuthToken } from "../../utils/auth";
import { getErrorMessage } from "../../config/errorConfig";

/** Current JWT from the existing auth flow, or null when the user isn't signed in. */
export function getWsAuthToken() {
  const token = getAuthToken();
  return typeof token === "string" && token.trim() !== "" ? token : null;
}

/** True when a JWT is available for WebSocket auth. */
export function hasWsAuthToken() {
  return getWsAuthToken() != null;
}

/** Append `token=<jwt>` to a WebSocket URL, whether or not it already has a query string. */
export function withAuthToken(url, token = getWsAuthToken()) {
  if (!token) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}

/** Existing "session expired" message, reused for missing/invalid JWT on the WS path. */
export const WS_AUTH_ERROR_MESSAGE = getErrorMessage("AUTH_SESSION_EXPIRED");

/**
 * Trigger the app's existing login flow — the same "openLoginModal" event
 * ChatBot.js dispatches whenever a chat action is attempted while signed out.
 */
export function requireWsAuth() {
  window.dispatchEvent(new CustomEvent("openLoginModal"));
}
